'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { css } from '@pt-group-fe/styled-system/css';
import { Box, Center, Stack, VStack } from '@pt-group-fe/styled-system/jsx';
import { cmsContentNewsletterSignup } from '@pt-group-fe/styled-system/recipes';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useLanguage, usePageProps } from '@lib/hooks';
import { contact } from '@lib/services/contact';
import { useTranslation } from '@lib/services/i18n/client';
import { FetchStatus } from '@lib/types/fetch-status';
import { Button } from '@lib/ui/button';
import { Form, FormField, FormInputText } from '@lib/ui/form';
import { Icon } from '@lib/ui/icon';
import { Text } from '@lib/ui/text';

import { useEnv } from '../envProvider';

interface SignupFormProps {
  formId: string;
  odooMailingListIds?: string;
}

const SignupForm: React.FC<SignupFormProps> = ({ odooMailingListIds }) => {
  const locale = useLanguage();
  const { captchaSiteKey } = useEnv();
  const [status, setStatus] = useState<FetchStatus>('idle');
  const [googleRecaptchaToken, setGoogleRecaptchaToken] = useState<string | null>();
  const catpchaRef = useRef<ReCAPTCHA>(null);
  const { t } = useTranslation();
  const { buttons, resMsg, resIcon, title } = cmsContentNewsletterSignup();
  const {
    params: { tenant }
  } = usePageProps();

  const formSchema = z.object({
    email: z
      .string({
        message: t('uiWeb.cmsContents.contact.field.fieldRquired', {
          name: t('uiWeb.cmsContents.contact.field.email')
        })
      })
      .email({
        message: t('uiWeb.cmsContents.contact.formItem.emailInvalid')
      }),
    firstname: z
      .string({
        message: t('uiWeb.cmsContents.contact.field.fieldRquired', {
          name: t('uiWeb.cmsContents.contact.field.firstName')
        })
      })
      .max(100),
    lastname: z
      .string({
        message: t('uiWeb.cmsContents.contact.field.fieldRquired', {
          name: t('uiWeb.cmsContents.contact.field.lastName')
        })
      })
      .max(100),
    company: z
      .string({
        message: t('uiWeb.cmsContents.contact.field.fieldRquired', {
          name: t('uiWeb.cmsContents.contact.field.company')
        })
      })
      .max(100)
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema as any),
    defaultValues: {
      email: '',
      company: '',
      firstname: '',
      lastname: ''
    }
  });

  const isBtnDisalbed = useMemo(() => {
    if (status === 'loading') return true;

    return googleRecaptchaToken === undefined;
  }, [googleRecaptchaToken, status]);

  const handleCaptcha = (token: string | null) => {
    setGoogleRecaptchaToken(token);
  };

  const handleReset = () => {
    setStatus('idle');
    form.reset();
    setGoogleRecaptchaToken(undefined);
    if (catpchaRef.current) {
      catpchaRef.current.reset();
    }
  };

  const onSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      if (!googleRecaptchaToken) return;

      setStatus('loading');

      // MAILING LIST ID 40
      setStatus('success');
      const isSuccess = await contact.subscribeToNewsletter({
        tenantUid: tenant,
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        company: values.company,
        mailingListIds: odooMailingListIds,
        googleRecaptchaToken
      });

      if (!isSuccess) {
        setStatus('error');

        return;
      }

      setStatus('success');
    },
    [googleRecaptchaToken, odooMailingListIds, tenant]
  );

  return (
    <Box w="full" mt={4} minH={360} pos="relative">
      {status !== 'success' && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack gap={4}>
              <FormField
                name="email"
                control={form.control}
                render={({ field }) => (
                  <FormInputText
                    isMui
                    label={t('uiWeb.cmsContents.contact.field.email')}
                    required
                    field={field as any}
                    {...field}
                    value={(field.value as string) || ''}
                  />
                )}
              />
              <FormField
                name="firstname"
                control={form.control}
                render={({ field }) => (
                  <FormInputText
                    field={field as any}
                    isMui
                    required
                    label={t('uiWeb.cmsContents.contact.field.firstname')}
                    {...field}
                    value={(field.value as string) || ''}
                  />
                )}
              />
              <FormField
                name="lastname"
                control={form.control}
                render={({ field }) => (
                  <FormInputText
                    field={field as any}
                    isMui
                    required
                    label={t('uiWeb.cmsContents.contact.field.lastname')}
                    {...field}
                    value={(field.value as string) || ''}
                  />
                )}
              />
              <FormField
                name="company"
                control={form.control}
                render={({ field }) => (
                  <FormInputText
                    field={field as any}
                    isMui
                    required
                    label={t('uiWeb.cmsContents.contact.field.company')}
                  />
                )}
              />

              {captchaSiteKey && (
                <Box minH="78px">
                  <ReCAPTCHA
                    sitekey={captchaSiteKey}
                    onChange={handleCaptcha}
                    ref={catpchaRef}
                    lang={locale}
                  />
                </Box>
              )}

              {status === 'error' && (
                <Box color="red.500">{t('uiWeb.cmsContents.contact.newsletterSignupError')}</Box>
              )}

              <div className={buttons}>
                <Button
                  size="default"
                  variant="primary"
                  icon="arrow-right"
                  type="submit"
                  loading={status === 'loading'}
                  disabled={isBtnDisalbed}
                >
                  {t('uiWeb.cmsContents.contact.btn.label')}
                </Button>
              </div>
            </Stack>
          </form>
        </Form>
      )}
      {status === 'success' && (
        <Center pos="absolute" inset={0} mt={-10}>
          <VStack>
            <Icon i="check-circle" className={resIcon} />
            <div className={title}>
              <Text variant="block" className={resMsg}>
                {t('uiWeb.cmsContents.contact.success')}
              </Text>
              <Text variant="block" className={resMsg}>
                {t('uiWeb.cmsContents.contact.newsletterSignupSuccessDesc')}
              </Text>
            </div>
            <Button
              className={css({ mt: 3 })}
              size="default"
              variant="secondary"
              onClick={handleReset}
            >
              {t('uiWeb.cmsContents.contact.succBtn.label')}
            </Button>
          </VStack>
        </Center>
      )}
    </Box>
  );
};

export { SignupForm };
